import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { useApp } from "@/contexts/AppContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Badge } from "../ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Label } from "../ui/label";
import {
  Plus,
  Shield,
  Lock,
  Unlock,
  Edit,
  Trash2,
  Search,
  Users,
  HelpCircle,
} from "lucide-react";
import { toast } from "sonner";
import {
  deleteUser,
  fetchAdminUsers,
  inviteUser,
  makeUserAdmin,
  setUserBlocked,
  updateUser,
} from "@/services/adminUser.service";
import type { AdminUserOverview } from "@/types/adminUser.types";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

/**
 * Admin panel view for managing users.
 */
export function UserManagementPanel() {
  const { t } = useApp();
  const iconStyle = {
    color:
      "color-mix(in srgb, color-mix(in srgb, var(--primary) 70%, var(--accent)) 90%, var(--foreground))",
  } as const;
  const primaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 77%, var(--surface))",
  } as const;
  const secondaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 66%, var(--surface))",
  } as const;
  const helpIconStyle = {
    color: "var(--help-icon-color)",
    "--help-icon-color":
      "color-mix(in srgb, color-mix(in srgb, var(--primary) 70%, var(--accent)) 90%, var(--foreground))",
    "--help-icon-hover":
      "color-mix(in srgb, var(--primary) 82%, var(--foreground))",
  } as CSSProperties;
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState<
    "all" | "admin" | "user" | "suspended"
  >("all");
  const [users, setUsers] = useState<AdminUserOverview[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [editingUser, setEditingUser] = useState<AdminUserOverview | null>(
    null
  );
  const [editForm, setEditForm] = useState({
    username: "",
    email: "",
    role: "user" as "user" | "admin",
    status: "active" as "active" | "suspended",
  });
  const [editOpen, setEditOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteEnglish, setInviteEnglish] = useState(false);
  const [isInviting, setIsInviting] = useState(false);

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const data = await fetchAdminUsers();
      setUsers(data);
    } catch (error) {
      toast.error(t("common.genericError"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    const term = searchQuery.trim().toLowerCase();
    return users.filter((user) => {
      const matchesSearch =
        !term ||
        user.username.toLowerCase().includes(term) ||
        user.email.toLowerCase().includes(term);
      if (!matchesSearch) return false;
      if (roleFilter === "admin") return user.isAdmin;
      if (roleFilter === "user") return !user.isAdmin;
      if (roleFilter === "suspended") return user.isBlocked;
      return true;
    });
  }, [users, searchQuery, roleFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedUsers = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredUsers.slice(start, start + pageSize);
  }, [filteredUsers, currentPage]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, roleFilter]);

  useEffect(() => {
    setPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  const handleDeleteUser = async (user: AdminUserOverview) => {
    try {
      await deleteUser(user.id);
      toast.success(t("admin.userDeleted"));
      await loadUsers();
    } catch (error) {
      toast.error(t("common.genericError"));
    }
  };

  const handleToggleBlock = async (user: AdminUserOverview) => {
    if (user.isAdmin) {
      return;
    }
    const nextBlocked = !user.isBlocked;
    try {
      await setUserBlocked(user.id, nextBlocked);
      toast.success(
        nextBlocked ? t("admin.userSuspended") : t("admin.userUnblocked")
      );
      await loadUsers();
    } catch (error) {
      toast.error(t("common.genericError"));
    }
  };

  const handleMakeAdmin = async (user: AdminUserOverview) => {
    try {
      await makeUserAdmin(user.id);
      toast.success(`${user.username} ${t("admin.userMadeAdmin")}`);
      await loadUsers();
    } catch (error) {
      toast.error(t("common.genericError"));
    }
  };

  const handleEditOpen = (user: AdminUserOverview) => {
    setEditingUser(user);
    setEditForm({
      username: user.username,
      email: user.email,
      role: user.isAdmin ? "admin" : "user",
      status: user.isBlocked ? "suspended" : "active",
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;
    const username = editForm.username.trim();
    const email = editForm.email.trim();
    if (!username || !email) {
      toast.error(t("common.genericError"));
      return;
    }
    const isAdmin = editingUser.isAdmin ? true : editForm.role === "admin";
    const isBlocked = editForm.status === "suspended";
    if (isAdmin && isBlocked) {
      toast.error(t("common.genericError"));
      return;
    }
    setIsSaving(true);
    try {
      await updateUser(editingUser.id, {
        username,
        email,
        isAdmin,
        isBlocked,
      });
      toast.success(t("admin.userUpdated"));
      setEditOpen(false);
      setEditingUser(null);
      await loadUsers();
    } catch (error) {
      toast.error(t("common.genericError"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendInvite = async () => {
    const normalizedEmail = inviteEmail.trim();
    if (!normalizedEmail) {
      toast.error(t("admin.inviteEmailRequired"));
      return;
    }
    setIsInviting(true);
    try {
      await inviteUser(normalizedEmail, inviteEnglish);
      toast.success(t("admin.inviteSent"));
      setInviteOpen(false);
      setInviteEmail("");
      setInviteEnglish(false);
    } catch (error) {
      toast.error(t("common.genericError"));
    } finally {
      setIsInviting(false);
    }
  };

  return (
    <Card className="stat-card">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <CardTitle
              className="card-title-text flex items-center gap-2"
              style={primaryTextStyle}
            >
              <Users className="w-5 h-5" style={iconStyle} />
              {t("admin.userManagement")}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="hover:[--help-icon-color:var(--help-icon-hover)]"
                    aria-label={t("admin.userManagementSelfHiddenHint")}
                    style={helpIconStyle}
                  >
                    <HelpCircle className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {t("admin.userManagementSelfHiddenHint")}
                </TooltipContent>
              </Tooltip>
            </CardTitle>
            <CardDescription
              className="card-description-text"
              style={secondaryTextStyle}
            >
              {t("admin.userManagementDescription")}
            </CardDescription>
          </div>
          <Button
            className="btn-primary gap-2 w-full sm:w-auto"
            onClick={() => setInviteOpen(true)}
          >
            <Plus className="w-4 h-4" />
            {t("admin.inviteUser")}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col sm:flex-row gap-2 mb-6">
          <div className="relative flex-1">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
              style={iconStyle}
            />
            <Input
              placeholder={t("admin.searchUsers")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select
            value={roleFilter}
            onValueChange={(value) => setRoleFilter(value as any)}
          >
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("admin.allUsersFilter")}</SelectItem>
              <SelectItem value="admin">{t("admin.adminsFilter")}</SelectItem>
              <SelectItem value="user">{t("admin.usersFilter")}</SelectItem>
              <SelectItem value="suspended">
                {t("admin.suspendedFilter")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[200px] text-secondary">
                  {t("admin.tableUser")}
                </TableHead>
                <TableHead className="min-w-[120px] text-secondary text-center">
                  {t("admin.tableRole")}
                </TableHead>
                <TableHead className="min-w-[120px] text-secondary text-center">
                  {t("admin.tableStatus")}
                </TableHead>
                <TableHead className="min-w-[120px] text-secondary text-center">
                  {t("admin.tableWebsites")}
                </TableHead>
                <TableHead className="min-w-[120px] text-secondary text-center">
                  {t("admin.tableJoined")}
                </TableHead>
                <TableHead className="min-w-[120px] text-secondary text-center">
                  {t("admin.tableLogin")}
                </TableHead>
                <TableHead className="text-right min-w-[150px] text-secondary">
                  {t("admin.tableActions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} style={secondaryTextStyle}>
                    {t("common.loading")}
                  </TableCell>
                </TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} style={secondaryTextStyle}>
                    {t("common.noResults")}
                  </TableCell>
                </TableRow>
              ) : (
                pagedUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div>
                        <p style={primaryTextStyle}>{user.username}</p>
                        <p className="text-sm" style={secondaryTextStyle}>
                          {user.email}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge
                        variant={user.isAdmin ? "default" : "secondary"}
                      >
                        {user.isAdmin ? t("admin.admin") : t("admin.user")}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge
                        variant={user.isBlocked ? "destructive" : "secondary"}
                      >
                        {user.isBlocked
                          ? t("admin.suspended")
                          : t("admin.active")}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center" style={secondaryTextStyle}>
                      {user.websiteCount}
                    </TableCell>
                    <TableCell className="text-center" style={secondaryTextStyle}>
                      {user.joinedAt
                        ? new Date(user.joinedAt).toLocaleDateString("de-DE")
                        : "-"}
                    </TableCell>
                    <TableCell className="text-center" style={secondaryTextStyle}>
                      {user.lastLoginAt
                        ? new Date(user.lastLoginAt).toLocaleDateString("de-DE")
                        : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end whitespace-nowrap">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleEditOpen(user)}
                        >
                          <Edit className="w-4 h-4" style={iconStyle} />
                        </Button>
                        {!user.isAdmin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleMakeAdmin(user)}
                          >
                            <Shield className="w-4 h-4" style={iconStyle} />
                          </Button>
                        )}
                        {!user.isAdmin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleToggleBlock(user)}
                          >
                            {user.isBlocked ? (
                              <Unlock className="w-4 h-4" style={iconStyle} />
                            ) : (
                              <Lock className="w-4 h-4" style={iconStyle} />
                            )}
                          </Button>
                        )}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                            >
                              <Trash2 className="w-4 h-4" style={iconStyle} />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                {t("admin.deleteUserConfirmTitle")}
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                {t("admin.deleteUserConfirmDescription").replace(
                                  "{username}",
                                  user.username
                                )}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>
                                {t("common.cancel")}
                              </AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteUser(user)}
                              >
                                {t("common.delete")}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4">
          <p className="text-xs" style={secondaryTextStyle}>
            {t("common.page")} {currentPage} / {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage <= 1}
            >
              {t("common.prev")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage >= totalPages}
            >
              {t("common.next")}
            </Button>
          </div>
        </div>
        <Dialog
          open={inviteOpen}
          onOpenChange={(open) => {
            setInviteOpen(open);
            if (!open) {
              setInviteEmail("");
              setInviteEnglish(false);
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("admin.inviteUser")}</DialogTitle>
              <DialogDescription>{t("admin.inviteUserDescription")}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{t("admin.inviteEmail")}</Label>
                <Input
                  value={inviteEmail}
                  onChange={(event) => setInviteEmail(event.target.value)}
                  placeholder={t("admin.inviteEmailPlaceholder")}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="invite-english"
                  type="checkbox"
                  checked={inviteEnglish}
                  onChange={(event) => setInviteEnglish(event.target.checked)}
                />
                <Label htmlFor="invite-english">
                  {t("admin.inviteEnglishLabel")}
                </Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setInviteOpen(false)}>
                {t("common.cancel")}
              </Button>
              <Button onClick={handleSendInvite} disabled={isInviting}>
                {t("admin.sendInvite")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog
          open={editOpen}
          onOpenChange={(open) => {
            setEditOpen(open);
            if (!open) {
              setEditingUser(null);
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("admin.editUserTitle")}</DialogTitle>
              <DialogDescription>{editingUser?.username}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{t("admin.username")}</Label>
                <Input
                  value={editForm.username}
                  onChange={(event) =>
                    setEditForm((prev) => ({
                      ...prev,
                      username: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>{t("admin.email")}</Label>
                <Input
                  value={editForm.email}
                  onChange={(event) =>
                    setEditForm((prev) => ({
                      ...prev,
                      email: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>{t("admin.role")}</Label>
                <Select
                  value={editForm.role}
                  disabled={editingUser?.isAdmin ?? false}
                  onValueChange={(value) =>
                    setEditForm((prev) => ({
                      ...prev,
                      role: value as "user" | "admin",
                      status: value === "admin" ? "active" : prev.status,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">{t("admin.user")}</SelectItem>
                    <SelectItem value="admin">{t("admin.admin")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("admin.status")}</Label>
                <Select
                  value={editForm.status}
                  onValueChange={(value) =>
                    setEditForm((prev) => ({
                      ...prev,
                      status: value as "active" | "suspended",
                    }))
                  }
                  disabled={editForm.role === "admin"}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">{t("admin.active")}</SelectItem>
                    <SelectItem value="suspended">
                      {t("admin.suspended")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditOpen(false)}>
                {t("common.cancel")}
              </Button>
              <Button
                onClick={handleSaveEdit}
                disabled={isSaving}
              >
                {t("common.save")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
